import {
  LayoutDashboard,
  CalendarRange,
  Car,
  Users,
  DollarSign,
  Wrench,
  MapPin,
  Settings,
  LogOut,
  Table2,
  Bell,
  Building2,
  ChevronDown,
} from "lucide-react";
import easyRentalLogo from "@/assets/easyrental-logo.png";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useBranches } from "@/hooks/useBranches";
import { useBranchContext } from "@/contexts/BranchContext";
import { useDemo } from "@/contexts/DemoContext";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const mainNav = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Bookings", url: "/bookings", icon: CalendarRange },
  { title: "Fleet", url: "/fleet", icon: Car },
];

const manageNav = [
  { title: "Customers", url: "/customers", icon: Users },
  { title: "Financials", url: "/financials", icon: DollarSign },
  { title: "Maintenance", url: "/maintenance", icon: Wrench },
  { title: "Availability", url: "/availability-table", icon: Table2 },
  { title: "Alerts", url: "/alerts", icon: Bell },
  { title: "Map", url: "/map", icon: MapPin },
];

const OWNER_ONLY_ROUTES = ["/financials", "/map"];

export function AppSidebar() {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { branches } = useBranches();
  const { activeBranchId, setActiveBranchId } = useBranchContext();
  const { isDemo, exitDemo } = useDemo();
  const { hasFeature } = useEntitlements();
  const { isOwner } = useRoleAccess();

  const visibleManageNav = manageNav.filter((item) => !OWNER_ONLY_ROUTES.includes(item.url) || isOwner);
  const showBranchSelector = hasFeature("multi_location") && branches.length > 0;
  const activeBranch = branches.find((b) => b.id === activeBranchId) ?? null;

  const handleSignOut = async () => {
    if (isDemo) {
      exitDemo();
      navigate("/", { replace: true });
      return;
    }

    await signOut();
    toast({ title: "Signed out", description: "You have been signed out." });
    navigate("/", { replace: true });
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <div className="flex h-14 items-center gap-3 border-b border-sidebar-border px-5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
        <img src={easyRentalLogo} alt="Easy Rental" className="h-9 w-auto shrink-0 object-contain" />
        <div className="flex flex-col group-data-[collapsible=icon]:hidden">
          <span className="text-sm font-display font-bold text-sidebar-accent-foreground">Easy Rental</span>
          <span className="text-[10px] uppercase tracking-wider text-sidebar-foreground/60">OS</span>
        </div>
      </div>

      {showBranchSelector && (
        <div className="border-b border-sidebar-border px-3 py-2 group-data-[collapsible=icon]:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-sidebar-foreground transition-colors hover:bg-sidebar-accent">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60" />
                <span className="flex-1 truncate text-left font-medium">
                  {activeBranch ? activeBranch.name : "All Branches"}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0 text-sidebar-foreground/40" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem
                className="gap-2 text-xs"
                onClick={() => setActiveBranchId(null)}
              >
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                All Branches
                {!activeBranchId && <span className="ml-auto text-[10px] font-medium text-primary">Active</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {branches.filter((b) => b.is_active).map((b) => (
                <DropdownMenuItem
                  key={b.id}
                  className="gap-2 text-xs"
                  onClick={() => setActiveBranchId(b.id)}
                >
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{b.name}</span>
                  {activeBranchId === b.id && <span className="ml-auto text-[10px] font-medium text-primary">Active</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <SidebarContent className="px-2 pt-4">
        <SidebarGroup>
          <SidebarGroupLabel className="mb-1 text-[10px] uppercase tracking-widest text-sidebar-foreground/40 group-data-[collapsible=icon]:hidden">
            Main
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/dashboard"}
                      data-tour={`nav-${item.url}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      activeClassName="bg-sidebar-accent font-medium text-sidebar-primary"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="mb-1 text-[10px] uppercase tracking-widest text-sidebar-foreground/40 group-data-[collapsible=icon]:hidden">
            Manage
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleManageNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      data-tour={`nav-${item.url}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      activeClassName="bg-sidebar-accent font-medium text-sidebar-primary"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        {profile && (
          <div className="mb-1 px-3 py-2 group-data-[collapsible=icon]:hidden">
            <p className="truncate text-xs font-medium text-sidebar-accent-foreground">{profile.full_name || profile.email}</p>
            <p className="truncate text-[10px] text-sidebar-foreground/50">{profile.email}</p>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Settings">
              <NavLink
                to="/settings"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                activeClassName="bg-sidebar-accent font-medium text-sidebar-primary"
              >
                <Settings className="h-4 w-4 shrink-0" />
                <span className="group-data-[collapsible=icon]:hidden">Settings</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={isDemo ? "Exit Demo" : "Sign Out"}
              onClick={handleSignOut}
              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">{isDemo ? "Exit Demo" : "Sign Out"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
